import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { hasRole, useAuth } from '../lib/auth';
import { when } from '../lib/labels';
import { ActionButton, ActionForm, ErrorBox, Loading, Page, Section, Stat, Table, Tabs, useApi } from '../ui/kit';

// Website visitors (src/visitors): which companies visit the customer's own website, company level only. The setup
// (owners and admins): the script with the site key, the install check, allowed domains, the EU consent rule and the
// privacy policy paragraph. Then the companies: pages, visits, last seen, the ICP match, Accounts and campaigns, with
// "Add to Accounts" and "Find decision makers" (qualifying first when the account is not qualified yet).

export type VisitorSettings = {
  configured: boolean; set_up: boolean; site_key: string | null; enabled: boolean; allowed_domains: string[]; eu_require_consent: boolean;
  installed: boolean; first_hit_at: string | null; first_hit_host: string | null; last_hit_at: string | null;
  snippet: string | null; pixel: string | null; disclosure: string; credits_per_company_day: number | null; vendor: string | null;
};
export type VisitorRow = {
  domain: string; name: string | null; country: string | null; employees: number | null; industry: string | null;
  visits: number; page_views: number; days_seen: number; first_seen_at: string; last_seen_at: string; fresh: boolean; pricing: boolean;
  top_pages: Array<{ path: string; views: number }>; referrers: string[];
  icp: { state: string; in_icp: boolean; reasons: string[] };
  account: { company_id: string; status: string | null } | null;
  campaigns: Array<{ id: string; name: string; status: string }>;
};
type Visitors = { days: number; data: VisitorRow[]; summary: { companies: number; in_icp: number; pricing: number; in_accounts: number; page_views: number; page_views_unidentified: number } };

export const ICP_LABEL: Record<string, string> = {
  qualified: 'Qualified', likely_fit: 'Likely fit', research_required: 'Needs research', disqualified: 'Disqualified',
  outside_icp: 'Outside ICP', unknown: 'Not enough data', no_icp: 'No approved ICP',
};
const REASON: Record<string, string> = { country: 'country', size: 'size', employees: 'size unknown', country_unknown: 'country unknown' };

function Copy({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return <button type="button" className="btn" onClick={() => { void navigator.clipboard?.writeText(text).then(() => setDone(true)); }}>{done ? 'Copied' : label}</button>;
}

export function VisitorSetup({ s, canEdit }: { s: VisitorSettings; canEdit: boolean }) {
  const domains = s.allowed_domains.join(', ');
  const fields = [
    { name: 'allowed_domains', label: 'Your website\'s domains', initial: domains, required: true, help: 'Comma-separated. Only page views on these domains (and their subdomains) count.' },
    { name: 'eu_require_consent', label: 'Count EU, EEA, UK and Swiss visitors only with consent', type: 'checkbox' as const, initial: s.eu_require_consent },
  ];
  const transform = (v: Record<string, unknown>) => ({ allowed_domains: String(v.allowed_domains ?? '').split(',').map((d) => d.trim()).filter(Boolean), eu_require_consent: Boolean(v.eu_require_consent) });
  if (!s.set_up) {
    return (
      <Section title="Set up website visitors" need={canEdit ? 'Set up' : undefined}>
        <p>See which companies visit your website. Company level only: Outlevo looks up the organization behind a visitor's network (for example their employer) and never identifies a person, sets no cookies and skips browsers that ask not to be tracked.</p>
        <p className="muted small">{s.credits_per_company_day ?? 1} credit per company identified per day, however many pages it reads. Home and mobile internet, VPNs and hosting are not companies and cost nothing.</p>
        {!s.configured ? <p className="banner" role="status">Website visitors is not available on this server yet: Outlevo's data provider is not connected.</p> : null}
        {canEdit ? <ActionForm title="Set up website visitors" button="Set up" variant="primary" method="PUT" path="/v1/visitors/settings" fields={fields} transform={transform} /> : <p className="muted">An owner or admin sets this up.</p>}
      </Section>
    );
  }
  return (
    <Section title="Setup" actions={canEdit ? <>
      <ActionForm title="Website visitors settings" button="Change" method="PUT" path="/v1/visitors/settings" fields={fields} transform={transform} />{' '}
      <ActionButton method="PUT" path="/v1/visitors/settings" body={{ enabled: !s.enabled }} label={s.enabled ? 'Pause' : 'Turn on'} confirm={s.enabled ? 'Stop counting page views? Nothing more is looked up until you turn it on.' : undefined} />
    </> : null}>
      {!s.configured ? <p className="banner" role="status">Outlevo's data provider is not connected on this server yet: page views are checked for the install, but no companies are identified.</p> : null}
      <p>
        <strong>Install check: </strong>
        {s.installed ? <>received the first page view from {s.first_hit_host} {when(s.first_hit_at!)}; last one {when(s.last_hit_at!)}.</> : <>waiting for the first page view from {domains}. Add the snippet, then open your website.</>}
        {!s.enabled ? ' Paused.' : ''}
      </p>
      <h3>1. Add this to every page, before &lt;/head&gt;</h3>
      <pre className="code" aria-label="Script snippet">{s.snippet}{'\n'}{s.pixel}</pre>
      <Copy text={`${s.snippet}\n${s.pixel}`} label="Copy the snippet" />
      <p className="muted small">The noscript pixel counts visitors without JavaScript. Allowed domains: {domains}.</p>
      <h3>2. Say so in your privacy policy</h3>
      <blockquote className="small">{s.disclosure}</blockquote>
      <Copy text={s.disclosure} label="Copy the paragraph" />
      <h3>3. Consent in the EU, EEA, UK and Switzerland</h3>
      <p className="small">{s.eu_require_consent
        ? <>Those visitors count only when your consent banner signals it: set <code>window.outlevoConsent = true</code> when the visitor agrees, then call <code>window.outlevoVisit()</code>. Without it their visits are dropped.</>
        : <>Those visitors count without a consent signal: your privacy policy must state the legal basis (the paragraph above does).</>}</p>
    </Section>
  );
}

const icpCell = (r: VisitorRow) => (
  <span className={`chip chip-${r.icp.in_icp ? 'good' : r.icp.state === 'outside_icp' || r.icp.state === 'disqualified' ? 'bad' : 'neutral'}`}
    title={r.icp.reasons.length ? `Because of: ${r.icp.reasons.map((x) => REASON[x] ?? x.replace(/_/g, ' ')).join(', ')}` : undefined}>
    {(ICP_LABEL[r.icp.state] ?? r.icp.state).toLowerCase()}
  </span>
);

function Act({ r }: { r: VisitorRow }) {
  if (!r.account) return <ActionButton path="/v1/visitors/accounts" body={{ domain: r.domain }} label="Add to Accounts" variant="primary" />;
  const qualified = r.account.status === 'qualified' || r.account.status === 'eligible_for_action';
  if (qualified) return <ActionButton path={`/v1/companies/${r.account.company_id}/contacts/discover?confirm=true`} body={{}} label="Find decision makers" confirm="Find decision makers here? This uses credits for contact discovery." />;
  if (r.account.status === 'disqualified') return <Link className="btn" to={`/accounts/${r.account.company_id}`}>Open the account</Link>;
  return <ActionButton path={`/v1/companies/${r.account.company_id}/qualify?confirm=true`} label="Find decision makers" confirm="It is not qualified yet: qualify it against your ICP first (credits)? Once qualified, find its decision makers here." />;
}

export function VisitorTable({ rows }: { rows: VisitorRow[] }) {
  return (
    <Table rows={rows} caption="Companies that visited" empty="No companies identified yet in this period."
      phone={{ title: (r) => r.name ?? r.domain, status: icpCell, meta: (r) => [r.domain, `${r.visits} ${r.visits === 1 ? 'visit' : 'visits'}`, r.pricing ? 'read pricing' : null, `last seen ${when(r.last_seen_at)}`] }}
      columns={[
        { key: 'name', label: 'Company', render: (r) => <><strong>{r.name ?? r.domain}</strong><div className="muted small">{[r.domain, r.country, r.employees ? `~${r.employees.toLocaleString('en-US')} people` : null, r.industry].filter(Boolean).join(' · ')}</div></> },
        { key: 'pages', label: 'Pages viewed', render: (r) => <>{r.top_pages.map((p) => `${p.path}${p.views > 1 ? ` (${p.views})` : ''}`).join(', ')}{r.pricing ? <> <span className="chip chip-good">pricing</span></> : null}</> },
        { key: 'visits', label: 'Visits', render: (r) => `${r.visits} · ${r.page_views} ${r.page_views === 1 ? 'page' : 'pages'}` },
        { key: 'last_seen_at', label: 'Last seen', render: (r) => when(r.last_seen_at) },
        { key: 'icp', label: 'ICP', render: icpCell },
        { key: 'where', label: 'In Outlevo', render: (r) => (r.account ? <><Link to={`/accounts/${r.account.company_id}`}>In Accounts</Link>{r.campaigns.length ? <div className="small">{r.campaigns.map((c) => <Link key={c.id} to={`/campaigns/${c.id}`}>{c.name}</Link>).reduce<ReactNode[]>((a, x, i) => (i ? [...a, ', ', x] : [x]), [])}</div> : null}</> : <span className="muted">Not yet</span>) },
        { key: 'act', label: '', render: (r) => <Act r={r} /> },
      ]} />
  );
}

export function VisitorsPage() {
  const { me } = useAuth();
  const [params] = useSearchParams();
  const days = Number(params.get('days') ?? 30) || 30;
  const icp = params.get('icp') === '1';
  const settings = useApi<VisitorSettings>('/v1/visitors/settings');
  const list = useApi<Visitors>(settings.data?.set_up ? `/v1/visitors?days=${days}${icp ? '&in_icp=true' : ''}` : null, { refetchMs: 60_000 });
  const canEdit = Boolean(me && hasRole(me, 'owner', 'admin'));
  const q = (d: number, i: boolean) => `?days=${d}${i ? '&icp=1' : ''}`;
  return (
    <Page title="Website visitors">
      <p className="muted">Companies that visited your website. Company level only: no person is identified, and visits from home or mobile internet are not companies, so not every visit has a name.</p>
      {settings.isLoading ? <Loading /> : settings.error ? <ErrorBox error={settings.error} retry={() => settings.refetch()} /> : (
        <>
          {settings.data!.set_up ? (
            <>
              <Tabs label="Period" tabs={[7, 30, 90].map((d) => ({ to: q(d, icp), label: `${d} days`, active: d === days }))} />
              <Tabs label="Which" tabs={[{ to: q(days, false), label: 'All companies', active: !icp }, { to: q(days, true), label: 'In your ICP', active: icp }]} />
              {list.isLoading ? <Loading /> : list.error ? <ErrorBox error={list.error} retry={() => list.refetch()} /> : list.data ? (
                <>
                  <div className="grid">
                    <Stat label="Companies" value={list.data.summary.companies} />
                    <Stat label="In your ICP" value={list.data.summary.in_icp} />
                    <Stat label="Read pricing" value={list.data.summary.pricing} />
                    <Stat label="Page views" value={list.data.summary.page_views} note={list.data.summary.page_views ? `${list.data.summary.page_views_unidentified} not from a company` : undefined} />
                  </div>
                  <Section title={icp ? 'Companies in your ICP' : 'Companies'}><VisitorTable rows={list.data.data} /></Section>
                </>
              ) : null}
            </>
          ) : null}
          <VisitorSetup s={settings.data!} canEdit={canEdit} />
        </>
      )}
    </Page>
  );
}
